import { memo } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  VisuallyHidden,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { useProgrammeList } from '@/api/queries';
import {
  PROGRAMME_STATUSES,
  type ProgrammeKind,
  type ProgrammeListItem,
  type ProgrammeStatus,
} from '@/api/types';
import { STATUS_LABEL } from '@/auth/permissions';
import { Artwork } from '@/components/Artwork';
import { EmptyState } from '@/components/EmptyState';
import { ListSearch } from '@/components/ListSearch';
import { ListFooter } from '@/components/PagedList';
import { CreatorSelect } from '@/components/pickers';
import { PageHeader } from '@/components/PageHeader';
import { ErrorAlert, PageLoader } from '@/components/QueryState';
import { StatusBadge } from '@/components/StatusBadge';
import { fromNow } from '@/lib/format';
import { useListUrlState } from '@/lib/useListUrlState';

export function ProgrammesPage() {
  const url = useListUrlState();
  const status = url.get('status') as ProgrammeStatus | undefined;
  const kind = url.get('kind') as ProgrammeKind | undefined;
  const creatorId = url.get('creator');
  const list = useProgrammeList({ status, kind, creator_id: creatorId, q: url.q });
  const [createOpen, createModal] = useDisclosure();
  const filtered = !!(status || kind || creatorId || url.q);

  return (
    <>
      <PageHeader
        title="Programmes"
        description="Series and standalone videos. Children only ever see published programmes that pass the eligibility rules."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={createModal.open}>
            New programme
          </Button>
        }
      />
      <Group gap="sm" mb="md" wrap="wrap" align="flex-end">
        <ListSearch
          label="Search programmes"
          placeholder="Search titles"
          value={url.q}
          onSearch={url.setQ}
        />
        <Select
          aria-label="Status"
          placeholder="Any status"
          clearable
          data={PROGRAMME_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
          value={status ?? null}
          onChange={(v) => url.setFilter('status', v)}
          w={160}
        />
        <SegmentedControl
          aria-label="Kind"
          size="sm"
          value={kind ?? 'all'}
          onChange={(v) => url.setFilter('kind', v === 'all' ? null : v)}
          data={[
            { value: 'all', label: 'All' },
            { value: 'series', label: 'Series' },
            { value: 'standalone', label: 'Standalone' },
          ]}
        />
        <CreatorSelect
          aria-label="Creator"
          placeholder="Any creator"
          clearable
          value={creatorId ?? null}
          onChange={(v) => url.setFilter('creator', v)}
          w={220}
        />
      </Group>

      {list.isPending ? (
        <PageLoader label="Loading programmes…" />
      ) : list.error ? (
        <ErrorAlert error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          title={filtered ? 'No programmes match' : 'No programmes yet'}
          action={
            <Button
              variant="default"
              leftSection={<IconPlus size={16} />}
              onClick={createModal.open}
            >
              New programme
            </Button>
          }
        >
          {filtered
            ? 'Try clearing a filter.'
            : 'Create the first programme to start building the catalogue.'}
        </EmptyState>
      ) : (
        <>
          <Table.ScrollContainer minWidth={760}>
            <Table striped stickyHeader style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={56}>
                    <VisuallyHidden>Poster</VisuallyHidden>
                  </Table.Th>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Checks</Table.Th>
                  <Table.Th>Episodes</Table.Th>
                  <Table.Th>Creator</Table.Th>
                  <Table.Th>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <ProgrammeRows items={list.data.items} />
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <ListFooter query={list} noun="programmes" />
        </>
      )}
      <CreateProgrammeModal opened={createOpen} onClose={createModal.close} />
    </>
  );
}

/** The table rows, memoised so typing or a filter change doesn't re-render every row. */
const ProgrammeRows = memo(function ProgrammeRows({ items }: { items: ProgrammeListItem[] }) {
  return (
    <>
      {items.map((p) => (
        <Table.Tr key={p.id}>
          <Table.Td>
            <Artwork image={p.poster} title={p.title} width={40} ratio={2 / 3} radius={6} />
          </Table.Td>
          <Table.Td>
            <Anchor component={Link} to={`/programmes/${p.id}`} fw={600} c="inherit">
              {p.title}
            </Anchor>
            <Text size="xs" c="dimmed">
              {p.kind === 'series' ? 'Series' : 'Standalone'}
            </Text>
          </Table.Td>
          <Table.Td>
            <StatusBadge status={p.status} scheduled={p.scheduled} />
          </Table.Td>
          <Table.Td>
            {p.issue_count > 0 ? (
              <Tooltip label="Things to fix before it can be approved or published">
                <Badge color="coral" variant="light" leftSection={<IconAlertCircle size={12} />}>
                  {p.issue_count} to fix
                </Badge>
              </Tooltip>
            ) : (
              <Badge color="leaf" variant="light" leftSection={<IconCircleCheck size={12} />}>
                Ready
              </Badge>
            )}
          </Table.Td>
          <Table.Td>
            <Text size="sm">
              {p.episode_count}
              <Text span size="xs" c="dimmed">
                {' '}
                · {p.ready_video_count} ready
              </Text>
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{p.creator?.name ?? '—'}</Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{fromNow(p.updated_at)}</Text>
            {p.updated_by && (
              <Text size="xs" c="dimmed">
                {p.updated_by}
              </Text>
            )}
          </Table.Td>
        </Table.Tr>
      ))}
    </>
  );
});

function CreateProgrammeModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const form = useForm({
    initialValues: {
      title: '',
      kind: 'series' as ProgrammeKind,
      creator_id: null as string | null,
    },
    validate: { title: (v) => (v.trim() ? null : 'Add a title.') },
  });
  const create = useMutation({
    mutationFn: (v: typeof form.values) =>
      adminApi.createProgramme({
        title: v.title.trim(),
        kind: v.kind,
        creator_id: v.creator_id ?? undefined,
      }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['programmes'] });
      form.reset();
      onClose();
      navigate(`/programmes/${p.id}`);
    },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="New programme">
      <form onSubmit={form.onSubmit((v) => create.mutate(v))}>
        <Stack>
          <TextInput label="Title" required data-autofocus {...form.getInputProps('title')} />
          <Stack gap={4}>
            <Text size="sm" fw={500} id="kind-label">
              Kind
            </Text>
            <SegmentedControl
              aria-labelledby="kind-label"
              data={[
                { value: 'series', label: 'Series (episodes)' },
                { value: 'standalone', label: 'Standalone video' },
              ]}
              {...form.getInputProps('kind')}
            />
            <Text size="xs" c="dimmed">
              A standalone programme has exactly one video. This can’t be changed later.
            </Text>
          </Stack>
          <CreatorSelect
            label="Creator"
            placeholder="Choose later"
            clearable
            value={form.values.creator_id}
            onChange={(v) => form.setFieldValue('creator_id', v)}
          />
          {create.error && (
            <Text c="coral.8" size="sm" role="alert">
              {errorMessage(create.error)}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create draft
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
